/**
 * Converts an array of objects to CSV format
 * @param data - Array of objects to convert
 * @param headers - Array of header keys to include (in order)
 * @param headerLabels - Optional mapping of keys to display labels
 * @param note - Optional note to display at the top of the CSV
 * @returns CSV string
 */
export function convertToCSV(
  data: Record<string, unknown>[],
  headers: string[],
  headerLabels?: Record<string, string>,
  note?: string
): string {
  if (!data || data.length === 0) {
    return "";
  }

  // Create note rows if provided
  let noteRows = "";
  if (note) {
    noteRows = note
      .split("\n")
      .map((line) => `"${line.replace(/"/g, '""')}"`)
      .join("\n") + "\n\n";
  }

  // Create header row
  const headerRow = headers
    .map((header) => {
      const label = headerLabels?.[header] || header;
      return `"${label}"`;
    })
    .join(",");

  // Create data rows
  const dataRows = data
    .map((row) => {
      return headers
        .map((header) => {
          let value = row[header];

          // Handle nested objects (e.g., product.name)
          if (header.includes(".")) {
            const keys = header.split(".");
            value = keys.reduce<unknown>((obj, key) => {
              if (obj !== null && obj !== undefined && typeof obj === "object") {
                return (obj as Record<string, unknown>)[key];
              }
              return undefined;
            }, row);
          }

          // Handle arrays (e.g., rfid_tags)
          if (Array.isArray(value)) {
            value = (value as unknown[]).map((item) => {
              if (typeof item === "object" && item !== null) {
                const record = item as Record<string, unknown>;
                return record.rfid || record.name || item;
              }
              return item;
            }).join("; ");
          }

          // Handle objects
          if (typeof value === "object" && value !== null) {
            value = JSON.stringify(value);
          }

          // Handle null/undefined
          if (value === null || value === undefined) {
            value = "";
          }

          // Escape quotes and wrap in quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",");
    })
    .join("\n");

  return `${noteRows}${headerRow}\n${dataRows}`;
}

/**
 * Triggers a download of CSV data
 * @param csvContent - The CSV string content
 * @param filename - The name of the file to download
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Exports data to CSV file
 * @param data - Array of objects to export
 * @param headers - Array of header keys to include
 * @param filename - Name of the CSV file
 * @param headerLabels - Optional mapping of keys to display labels
 * @param note - Optional note to display at the top of the CSV
 */
export function exportToCSV(
  data: Record<string, unknown>[],
  headers: string[],
  filename: string,
  headerLabels?: Record<string, string>,
  note?: string
): void {
  const csvContent = convertToCSV(data, headers, headerLabels, note);
  if (csvContent) {
    downloadCSV(csvContent, filename);
  }
}
